import React, { useMemo } from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { TFunction } from 'i18next';
import { IGeogridData, IGeogridItem } from '../../../../../api/IApiResponse';
import { ProductDataService } from '../../../../../services/ProductDataService';
import { getTensileAtStrain } from '../../../../../services/TensileAtStrength';
import { calculateGeogridRequiredStrengthUnified, calculateGeogridStrengthBreakdownUnified } from '../../../../../services/GeogridTtotService';
import { formatSubscriptString } from '../../../../../enum/SubscriptSymbol';

// Funzione di parsing base per <br> e <strong>
import { ValueUnitCells } from '../../shared/pdfUnitMerge';

function parseHtmlToPdfText(html: string, style?: any) {
    // Sostituisce <br> con \n
    const withBreaks = html.replace(/<br\s*\/??\s*>/gi, '\n');
    // Regex per trovare <strong>...</strong>
    const regex = /(<strong>(.*?)<\/strong>)/gi;
    const result: JSX.Element[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(withBreaks)) !== null) {
        // Testo prima del tag
        if (match.index > lastIndex) {
            const text = withBreaks.substring(lastIndex, match.index);
            if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
        }
        // Testo in grassetto
        const boldText = match[2];
        if (boldText) {
            result.push(
                <Text
                    key={key++}
                    style={{
                        ...style,
                        fontFamily: 'DejaVuSans',
                        fontWeight: 'bold',
                    }}
                >
                    {boldText}
                </Text>
            );
        }
        lastIndex = regex.lastIndex;
    }
    // Testo dopo l'ultimo tag
    if (lastIndex < withBreaks.length) {
        const text = withBreaks.substring(lastIndex);
        if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
    }
    return result;
}
interface AirportGeoGridCoreProps {
    geoGridData?: IGeogridData;
    geogridImage?: string;
    projectData: any;
    t: TFunction;
    showInputData?: boolean;
    showGeoGridImage?: boolean;
    showCourseResults?: boolean;
    customTitle?: string;
}

export const AirportGeoGridCore: React.FC<AirportGeoGridCoreProps> = ({
    geoGridData,
    geogridImage,
    projectData,
    t,
    showInputData = true,
    showGeoGridImage = true,
    showCourseResults = true,
    customTitle,
}) => {
    const unitSystem = projectData?.unit || 'Metric';
    const projectType = projectData?.type || 'geogrid';
    const projectGeoGridData = projectData?.projectInfo?.data?.geoGrid;

    // Extract GeoGrid course data
    const baseThickness = geoGridData?.geogridBaseCourse?.thickness ?? 0;
    const subbaseThickness = geoGridData?.geogridSubBaseCourse?.thickness ?? 0;
    const baseGeogrids = geoGridData?.geogridBaseCourse?.geogrids ?? [];
    const subbaseGeogrids = geoGridData?.geogridSubBaseCourse?.geogrids ?? [];
    const tipoGeoGrid = geoGridData?.type;

    // Helper function to get unit based on system
    const getUnit = (metricUnit: string, imperialUnit: string) => {
        return unitSystem === 'Imperial' ? imperialUnit : metricUnit;
    };

    // Helper to format numbers to 2 decimal places
    const formatValue = (value: unknown): string => {
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        return String(value);
    };

    // Format course data for PDF table
    const formatCourseDataForPdfTable = (
        courseTitleKey: string,
        thickness: number,
        geogrids: IGeogridItem[],
        t: TFunction,
        projectGeoGridData?: IGeogridData,
        getUnitFn?: (metricUnit: string, imperialUnit: string) => string
    ): (string[] | { type: 'fullRow'; text: string })[] => {
        const tableData: (string[] | { type: 'fullRow'; text: string })[] = [];
        const courseName = t(courseTitleKey);
        // Use provided getUnit function or default to metric
        const unitFn = getUnitFn || ((metric: string) => metric);

        tableData.push([t('graphic.table.course_thickness', { course: courseName }) || `${courseName} Thickness`, 't', thickness.toFixed(2), unitFn('m', 'ft')]);

        geogrids.forEach((geogrid, index) => {
            const geogridNum = index + 1;
            const productFamily = geogrid.type;
            const productName = geogrid.selectedGeogrid;
            const designStrain = geogrid.designStrain;

            const courseType: 'base' | 'subbase' = courseTitleKey.includes('subbase') ? 'subbase' : 'base';
            const calculatedRequiredStrength = calculateGeogridRequiredStrengthUnified(projectGeoGridData, index, courseType);
            const calculatedNominalStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);
            const fallbackRequiredStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);

            // Get tensile breakdown (Tz, Tp, Tm)
            const breakdown = calculateGeogridStrengthBreakdownUnified(projectGeoGridData, index, courseType);

            const numericReqStrength = calculatedRequiredStrength ?? fallbackRequiredStrength;
            const numericNomStrength = calculatedNominalStrength;

            const displayRequiredStrength = numericReqStrength?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A';
            const displayNominalStrength = numericNomStrength?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A';

            tableData.push([t('graphic.table.geogrid_product', { num: geogridNum }), `Product ${geogridNum}`, geogrid.selectedGeogrid || t('common.not_available', 'N/A'), '']);
            tableData.push([t('graphic.table.geogrid_position', { num: geogridNum }), formatSubscriptString(`p_${geogridNum}`), geogrid.position?.toFixed(2) || t('common.not_available', 'N/A'), unitFn('m', 'ft')]);
            tableData.push([t('graphic.table.geogrid_strain', { num: geogridNum }), formatSubscriptString(`ε_d${geogridNum}`), (geogrid.designStrain?.toString() || t('common.not_available', 'N/A')) ?? 'N/A', '%']);
            // Tensile contribution breakdown rows
            tableData.push([t('graphic.table.geogrid_earth_pressure'), formatSubscriptString(`T_z${geogridNum}`), breakdown?.Tz?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A', unitFn('kN/m', 'lbf/ft')]);
            tableData.push([t('graphic.table.geogrid_tension_component'), formatSubscriptString(`T_p${geogridNum}`), breakdown?.Tp?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A', unitFn('kN/m', 'lbf/ft')]);
            tableData.push([t('graphic.table.geogrid_membrane_effect'), formatSubscriptString(`T_m${geogridNum}`), breakdown?.Tm?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A', unitFn('kN/m', 'lbf/ft')]);
            tableData.push([t('graphic.table.geogrid_req_strength', { num: geogridNum }), formatSubscriptString(`T_req${geogridNum}`), displayRequiredStrength, unitFn('kN/m', 'lbf/ft')]);
            tableData.push([t('graphic.table.geogrid_nom_strength', { num: geogridNum }), formatSubscriptString(`T_nom${geogridNum}`), displayNominalStrength, unitFn('kN/m', 'lbf/ft')]);

            let statusText: string;
            if (numericNomStrength !== null && numericReqStrength !== null && numericNomStrength >= numericReqStrength) {
                statusText = t('graphic.table.verified', 'Verified');
            } else if (numericNomStrength === null || numericReqStrength === null) {
                statusText = t('graphic.table.data_missing_for_verification', 'Data Missing for Verification');
            } else {
                statusText = t('graphic.table.not_verified', 'Not Verified');
            }
            tableData.push({ type: 'fullRow', text: statusText });
        });

        return tableData;
    };

    const formattedBaseCourseData = useMemo(
        () => formatCourseDataForPdfTable('geogrid_base_course', baseThickness, baseGeogrids, t, projectGeoGridData, getUnit),
        [baseThickness, baseGeogrids, t, projectGeoGridData, getUnit]
    );

    const formattedSubbaseCourseData = useMemo(
        () => formatCourseDataForPdfTable('geogrid_subbase_course', subbaseThickness, subbaseGeogrids, t, projectGeoGridData, getUnit),
        [subbaseThickness, subbaseGeogrids, t, projectGeoGridData, getUnit]
    );

    return (
        <>
            {/* Title Section */}
            {/* Project Type Header */}
            <View style={pdfSharedStyles.spacer1} />
            <Text style={[pdfSharedStyles.projectInfoProjectType]}>GROUNDMAC - Airport modified</Text>
            <View style={pdfSharedStyles.spacer0} />
            <Text>{parseHtmlToPdfText(t('airport_GeoGrid_project_info_desc_pdf'), pdfSharedStyles.projectInfoSubtitle)}</Text>

            {/* Input Data Summary Section */}
            {showInputData && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, { textAlign: 'left' }]}>{t('input_data_summary')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.innSection}>
                        {/* Load Table */}
                        {geoGridData?.load && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('load')}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {tipoGeoGrid?.geogridType === 'Rectangular' ? (
                                        <>
                                            {geoGridData.load.deckHalfWidth !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.deckHalfWidth')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>B</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.deckHalfWidth)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                            {geoGridData.load.deckHalfLength !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.deckHalfLength')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>L</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.deckHalfLength)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                            {geoGridData.load.uniformLoad !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.uniformLoad')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>q</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.uniformLoad)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psf')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {geoGridData.load.axleLoad !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.axleLoad')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>P</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.axleLoad)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('kN', 'lbf')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                            {geoGridData.load.tireInflationPressure !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.tireInflationPressure')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>p</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.tireInflationPressure)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psi')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                            {geoGridData.load.equivalentStandardAxleLoad !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.equivalentStandardAxleLoad')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>Pa</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.equivalentStandardAxleLoad)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('kN', 'lbf')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                        </>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Truck Load For First Lift Table */}
                        {geoGridData?.truck && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('truck')}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {geoGridData.truck.wheelRadius !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('truck.wheelRadius')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>rw</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.truck.wheelRadius)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.truck.tirePressure !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('truck.tirePressure')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>pw</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.truck.tirePressure)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psi')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.truck.thickness !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('truck.thickness')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>h*</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.truck.thickness)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Surface Layer Table */}
                        {geoGridData?.surfaceLayer && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('surfaceLayer')}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {geoGridData.surfaceLayer.thickness !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('surfaceLayer.thickness')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>h</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.surfaceLayer.thickness)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.surfaceLayer.unitWeight !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('surfaceLayer.unitWeight')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>γ</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.surfaceLayer.unitWeight)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kN/m³', 'pcf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.surfaceLayer.loadSpreadingAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('surfaceLayer.loadSpreadingAngle')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>α</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.surfaceLayer.loadSpreadingAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Base Course Table */}
                        {geoGridData?.baseCourse && baseThickness > 0 && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('baseCourse')}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {geoGridData.baseCourse.unitWeight !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('baseCourse.unitWeight')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>γ</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.baseCourse.unitWeight)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kN/m³', 'pcf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.baseCourse.frictionAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('baseCourse.frictionAngle')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>φ</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.baseCourse.frictionAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.baseCourse.loadSpreadingAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('baseCourse.loadSpreadingAngle')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>α</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.baseCourse.loadSpreadingAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.baseCourse.cohesion !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('baseCourse.cohesion')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>c</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.baseCourse.cohesion)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* SubBase Course Table */}
                        {geoGridData?.subBaseCourse && subbaseThickness > 0 && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('subBaseCourse')}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {geoGridData.subBaseCourse.unitWeight !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subBaseCourse.unitWeight')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>γ</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subBaseCourse.unitWeight)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kN/m³', 'pcf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.subBaseCourse.frictionAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subBaseCourse.frictionAngle')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>φ</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subBaseCourse.frictionAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.subBaseCourse.loadSpreadingAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subBaseCourse.loadSpreadingAngle')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>α</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subBaseCourse.loadSpreadingAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.subBaseCourse.cohesion !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subBaseCourse.cohesion')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>c</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subBaseCourse.cohesion)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Subgrade Table */}
                        {geoGridData?.subgrade && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('subgrade')}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {/* Show CBR only if subgradeInputMethod is 'cbr' */}
                                    {geoGridData.subgrade.subgradeInputMethod === 'cbr' && geoGridData.subgrade.cbrSubgrade !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subgrade.cbrSubgrade')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>CBR</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subgrade.cbrSubgrade)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>%</Text>
                                            </View>
                                        </View>
                                    )}
                                    {/* Show Ev2 only if subgradeInputMethod is 'ev2' or 'modulus' */}
                                    {(geoGridData.subgrade.subgradeInputMethod === 'ev2' || geoGridData.subgrade.subgradeInputMethod === 'modulus') &&
                                        geoGridData.subgrade.deformationModulus !== undefined && (
                                            <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                                <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                    <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subgrade.deformationModulus') || 'Deformation Modulus'}</Text>
                                                </View>
                                                <View style={pdfSharedStyles.tableColSymbol}>
                                                    <Text style={pdfSharedStyles.tableCell}>Ev2</Text>
                                                </View>
                                                <View style={pdfSharedStyles.tableColValue}>
                                                    <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subgrade.deformationModulus)}</Text>
                                                </View>
                                                <View style={pdfSharedStyles.tableColUnit}>
                                                    <Text style={pdfSharedStyles.tableCell}>{getUnit('MPa', 'psi')}</Text>
                                                </View>
                                            </View>
                                        )}
                                    {geoGridData.subgrade.safetyFactor !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subgrade.safetyFactor')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>FS</Text>
                                            </View>
                                            <ValueUnitCells value={formatValue(geoGridData.subgrade.safetyFactor)} unit="-" />
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}
                    </View>
                </View>
            )}

            <View break />

            {/* Results Section */}
            <View style={pdfSharedStyles.spacer3} />
            <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results') || 'Results'}</Text>

            {/* GeoGrid Layer Visualization */}
            {showGeoGridImage && geogridImage && (
                <View style={pdfSharedStyles.section} wrap={false}>
                    <Image style={pdfSharedStyles.graphicImage} src={geogridImage} />
                </View>
            )}

            {/* Base Course Results */}
            {showCourseResults && baseThickness > 0 && baseGeogrids.length > 0 && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={pdfSharedStyles.tableTitle}>{t('geogrid_base_course', 'Base Course Details')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.table}>
                        <View style={pdfSharedStyles.tableRow} fixed>
                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                            </View>
                        </View>
                        {formattedBaseCourseData.map((rowItem, rowIndex) => {
                            if (Array.isArray(rowItem)) {
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{rowItem[0]}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColSymbol}>
                                            <Text style={pdfSharedStyles.tableCell}>{rowItem[1]}</Text>
                                        </View>
                                        <ValueUnitCells value={rowItem[2]} unit={rowItem[3]} />
                                    </View>
                                );
                            } else {
                                // Full row for status
                                const isVerified = !rowItem.text.includes('Not Verified');
                                return (
                                    <View key={rowIndex} style={pdfSharedStyles.tableRow} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, isVerified ? pdfSharedStyles.verifiedCol : pdfSharedStyles.notVerifiedCol]}>
                                            <Text style={{ ...pdfSharedStyles.verifiedColTableCell, textAlign: 'left', paddingLeft: 10 }}>{rowItem.text}</Text>
                                        </View>
                                    </View>
                                );
                            }
                        })}
                    </View>
                </View>
            )}

            {/* Subbase Course Results */}
            {showCourseResults && subbaseThickness > 0 && subbaseGeogrids.length > 0 && (
                <View style={pdfSharedStyles.section}>
                    <View break />
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={pdfSharedStyles.tableTitle}>{t('geogrid_subbase_course', 'Subbase Course Details')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.table}>
                        <View style={pdfSharedStyles.tableRow} fixed>
                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                            </View>
                        </View>
                        {formattedSubbaseCourseData.map((rowItem, rowIndex) => {
                            if (Array.isArray(rowItem)) {
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{rowItem[0]}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColSymbol}>
                                            <Text style={pdfSharedStyles.tableCell}>{rowItem[1]}</Text>
                                        </View>
                                        <ValueUnitCells value={rowItem[2]} unit={rowItem[3]} />
                                    </View>
                                );
                            } else {
                                // Full row for status
                                const isVerified = !rowItem.text.includes('Not Verified');
                                return (
                                    <View key={rowIndex} style={pdfSharedStyles.tableRow} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, isVerified ? pdfSharedStyles.verifiedCol : pdfSharedStyles.notVerifiedCol]}>
                                            <Text style={{ ...pdfSharedStyles.verifiedColTableCell, textAlign: 'left', paddingLeft: 10 }}>{rowItem.text}</Text>
                                        </View>
                                    </View>
                                );
                            }
                        })}
                    </View>
                </View>
            )}
        </>
    );
};
